/**
 * =============================================================================
 * Hook: useReferralCommissionsAdmin
 * Versão: 1.0.0 (Fase 5 — Bloco 5E, item 5.10)
 *
 * Descrição:
 *   Hooks administrativos (visão TENANT) para a tabela `affiliate_commissions`.
 *   Diferente do hook do painel do afiliado (`useAffiliateCommissions`), este
 *   busca TODAS as comissões do tenant ativo, com filtros e mutations de
 *   moderação (aprovar / cancelar / marcar como paga manualmente).
 *
 *   - Toda leitura é restrita por `tenant_id` + RLS no banco.
 *   - Mutations atualizam apenas campos administrativos (status, datas,
 *     motivo de cancelamento, notas internas) — não tocam em payouts/saldos.
 *
 * IMPORTANTE: Este hook NÃO interage com o módulo Financeiro. Todo o fluxo
 * permanece dentro do escopo do módulo Indicação (`affiliate_*`).
 * =============================================================================
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { Database } from '@/integrations/supabase/types';

const log = createLogger('useReferralCommissionsAdmin');

export type CommissionStatus =
  Database['public']['Enums']['affiliate_commission_status'];
export type RewardType =
  Database['public']['Enums']['affiliate_reward_type'];

export interface AdminCommissionRow {
  id: string;
  status: CommissionStatus;
  reward_type: RewardType;
  reward_value: number;
  base_value: number;
  currency: string;
  points_earned: number;
  created_at: string;
  approved_at: string | null;
  paid_at: string | null;
  cancelled_at: string | null;
  cancelled_reason: string | null;
  notes: string | null;
  payout_id: string | null;
  affiliate_id: string;
  referral_id: string;
  campaign_id: string | null;
  affiliate: { id: string; full_name: string; email: string } | null;
  referral: { id: string; lead_name: string | null } | null;
  campaign: { id: string; name: string } | null;
}

export interface UseReferralCommissionsFilters {
  /** 'all' (default) ou um status específico. */
  status?: CommissionStatus | 'all';
  /** Filtro por afiliado. */
  affiliateId?: string | 'all';
  /** Busca livre (nome do lead / nome do afiliado). */
  search?: string;
}

const QK = (
  tenantId: string | null | undefined,
  filters: UseReferralCommissionsFilters,
) => ['referral-commissions-admin', tenantId, filters];

/**
 * Lista comissões do tenant ativo com joins enxutos para exibição em tabela.
 */
export function useReferralCommissionsAdmin(
  filters: UseReferralCommissionsFilters = {},
) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  const normalized = useMemo<UseReferralCommissionsFilters>(
    () => ({
      status: filters.status ?? 'all',
      affiliateId: filters.affiliateId ?? 'all',
      search: filters.search?.trim() || undefined,
    }),
    [filters.status, filters.affiliateId, filters.search],
  );

  return useQuery<AdminCommissionRow[]>({
    queryKey: QK(tenantId, normalized),
    enabled: !!tenantId,
    queryFn: async () => {
      let query = supabase
        .from('affiliate_commissions')
        .select(
          `
            id,
            status,
            reward_type,
            reward_value,
            base_value,
            currency,
            points_earned,
            created_at,
            approved_at,
            paid_at,
            cancelled_at,
            cancelled_reason,
            notes,
            payout_id,
            affiliate_id,
            referral_id,
            campaign_id,
            affiliate:affiliates ( id, full_name, email ),
            referral:affiliate_referrals ( id, lead_name ),
            campaign:affiliate_campaigns ( id, name )
          `,
        )
        .eq('tenant_id', tenantId!)
        .order('created_at', { ascending: false })
        .limit(500);

      if (normalized.status && normalized.status !== 'all') {
        query = query.eq('status', normalized.status);
      }
      if (normalized.affiliateId && normalized.affiliateId !== 'all') {
        query = query.eq('affiliate_id', normalized.affiliateId);
      }

      const { data, error } = await query;
      if (error) {
        log.error('Erro ao listar comissões (admin)', error);
        throw error;
      }

      // Normaliza joins (Supabase pode retornar array em FKs ambíguas)
      const rows = (data ?? []).map((row: any) => ({
        ...row,
        affiliate: Array.isArray(row.affiliate)
          ? row.affiliate[0] ?? null
          : row.affiliate,
        referral: Array.isArray(row.referral)
          ? row.referral[0] ?? null
          : row.referral,
        campaign: Array.isArray(row.campaign)
          ? row.campaign[0] ?? null
          : row.campaign,
      })) as AdminCommissionRow[];

      // Busca client-side (lead_name + nome do afiliado)
      const s = normalized.search?.toLowerCase();
      if (!s) return rows;
      return rows.filter((r) => {
        const lead = r.referral?.lead_name?.toLowerCase() ?? '';
        const aff = r.affiliate?.full_name?.toLowerCase() ?? '';
        const email = r.affiliate?.email?.toLowerCase() ?? '';
        return lead.includes(s) || aff.includes(s) || email.includes(s);
      });
    },
    staleTime: 30_000,
  });
}

/**
 * Mutations administrativas para comissões.
 * Aprovar / cancelar / marcar como paga manualmente.
 */
export function useReferralCommissionMutations() {
  const qc = useQueryClient();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['referral-commissions-admin'] });
    // v1.1.0: invalida também a nova queryKey paginada server-side
    qc.invalidateQueries({ queryKey: ['referral-commissions-paginated'] });
    qc.invalidateQueries({ queryKey: ['referral-dashboard-kpis'] });
  };

  /**
   * Aprova uma comissão pendente via RPC `approve_commission`.
   * A RPC valida tenant, papel admin e estado (pending) atomicamente.
   */
  const approveMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!tenantId) throw new Error('Tenant ativo não definido');
      const { data, error } = await supabase.rpc('approve_commission', {
        p_commission_id: id,
        p_notes: null,
        p_tenant_hint: tenantId,
      });
      if (error) {
        log.error('Falha ao aprovar comissão', error);
        throw error;
      }
      return data as string;
    },
    onSuccess: invalidate,
  });

  /**
   * Cancela uma comissão (com motivo) via RPC `reject_commission`.
   * A RPC garante que comissões já pagas não sejam canceladas e
   * desvincula automaticamente de payouts pendentes.
   */
  const cancelMutation = useMutation({
    mutationFn: async (params: { id: string; reason: string | null }) => {
      if (!tenantId) throw new Error('Tenant ativo não definido');
      const { data, error } = await supabase.rpc('reject_commission', {
        p_commission_id: params.id,
        p_reason: params.reason ?? '',
        p_tenant_hint: tenantId,
      });
      if (error) {
        log.error('Falha ao cancelar comissão', error);
        throw error;
      }
      return data as string;
    },
    onSuccess: invalidate,
  });

  /**
   * @deprecated v2.0.0 (2026-04-29)
   * A RPC `mark_commission_paid_manual` foi descontinuada para garantir
   * que toda comissão paga apareça no extrato (`affiliate_payouts`).
   * Use `useCreatePayoutForCommission` + a tela Pagamentos.
   *
   * Mantido apenas para compatibilidade — chamadas agora lançam exceção
   * no banco com mensagem clara.
   */
  const markPaidMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!tenantId) throw new Error('Tenant ativo não definido');
      const { data, error } = await supabase.rpc(
        'mark_commission_paid_manual',
        {
          p_commission_id: id,
          p_notes: null,
          p_tenant_hint: tenantId,
        },
      );
      if (error) {
        log.error('Falha ao marcar comissão como paga (DEPRECATED)', error);
        throw error;
      }
      return data as string;
    },
    onSuccess: invalidate,
  });

  /** Atualiza apenas as notas internas. */
  const updateNotesMutation = useMutation({
    mutationFn: async (params: { id: string; notes: string | null }) => {
      if (!tenantId) throw new Error('Tenant ativo não definido');
      const { error } = await supabase
        .from('affiliate_commissions')
        .update({ notes: params.notes })
        .eq('id', params.id)
        .eq('tenant_id', tenantId);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return {
    approveMutation,
    cancelMutation,
    markPaidMutation,
    updateNotesMutation,
  };
}
